import React, { useEffect, useState } from 'react';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import {
  Tag, Divider, Typography, Flex,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import StatusBadge from '../../../components/Badges/StatusBadge';
import HeaderLabel from '../../../components/Form/HeaderLabel';
import AlumlyBreadCrumb, { PathT } from '../../../components/AlumlyBreadCrumb';
import AlumlyButton from '../../../components/AlumlyButton';
import { PermissionEnum } from '../../../enums/permission';
import { RouteNameEnum } from '../../../enums/routeName';
import { ServiceNameEnum } from '../../../enums/serviceName';
import usePermission from '../../../hooks/usePermission';
import useServices from '../../../hooks/useServices';
import { AxiosService } from '../../../utils/axiosService';
import { PathBuilder } from '../../../utils/pathBuilder';
import DeleteFeaturedContentModal from '../components/DeleteFeaturedContentModal';

const FeaturedContentDetail: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { checkPermission } = usePermission();
  const [detail, setDetail] = useState<any>(null);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const { service: getDetailService } = useServices(ServiceNameEnum.featuredContent.detail);

  const getDetail = async (): Promise<void> => {
    const params = { id };
    const { data, error } = await getDetailService.get({ params });
    if (data) {
      setDetail(data);
    } else if (error) {
      const errorResponse = AxiosService.getErrorResponse(error);
      toast.error(errorResponse?.message, { toastId: 'allToast' });
      navigate(RouteNameEnum.featuredContent.index);
    }
  };

  useEffect(() => { getDetail(); }, []);

  const path: PathT[] = [
    { label: 'Featured Contents', value: RouteNameEnum.featuredContent.index },
    { label: 'Featured content detail' },
  ];

  const getML = (ml: any, key: string): string => ml?.[key] || '-';

  // Resolve from linked event or news
  const source = detail?.type === 'event' ? detail?.event : detail?.news;
  const resolvedTitleML = source?.titleML;
  const resolvedCoverUrl = source?.coverFile?.presignUrl || null;

  return (
    <div>
      <AlumlyBreadCrumb path={path} />
      <Typography.Title level={4} style={{ margin: 0 }}>Featured Content Detail</Typography.Title>
      <Flex align="center" justify="space-between">
        <Flex align="center" gap={16}>
          <Typography.Text type="secondary">status</Typography.Text>
          <StatusBadge status={!!detail?.isPublished} />
          <Tag color={detail?.type === 'event' ? 'blue' : 'magenta'}>{detail?.type || '-'}</Tag>
        </Flex>
        <Flex gap={16}>
          {checkPermission([PermissionEnum.code.featuredContentDelete]) && (
            <AlumlyButton label="rxvWhite" icon={<DeleteOutlined />} width={200} onClick={() => setOpenDeleteModal(true)}>
              Delete
            </AlumlyButton>
          )}
          {checkPermission([PermissionEnum.code.featuredContentEdit]) && (
            <AlumlyButton label="rxv" icon={<EditOutlined />} width={200} onClick={() => navigate(PathBuilder.buildPath(RouteNameEnum.featuredContent.edit, { params: { id } }))}>
              Edit
            </AlumlyButton>
          )}
        </Flex>
      </Flex>

      <DeleteFeaturedContentModal
        featuredContentId={Number(id)}
        isOpen={openDeleteModal}
        handleClose={() => setOpenDeleteModal(false)}
      />

      {/* Linked Content */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label={detail?.type === 'event' ? 'Linked Event' : 'Linked News'} />
        <div style={{ background: 'white' }}>
          {resolvedCoverUrl && (
            <>
              <div style={{ paddingLeft: 16, paddingTop: 16 }}>
                <Typography.Text type="secondary">Cover Image</Typography.Text>
                <div>
                  <img
                    src={resolvedCoverUrl}
                    alt=""
                    style={{ width: 300, height: 200, marginTop: 8, borderRadius: 4, objectFit: 'cover' }}
                  />
                </div>
              </div>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            </>
          )}
          <div style={{ paddingLeft: 16, marginTop: 16 }}>
            <Typography.Text type="secondary">Title (EN)</Typography.Text>
            <div><Typography.Text>{getML(resolvedTitleML, 'en')}</Typography.Text></div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          <div style={{ paddingLeft: 16 }}>
            <Typography.Text type="secondary">Title (TH)</Typography.Text>
            <div><Typography.Text>{getML(resolvedTitleML, 'th')}</Typography.Text></div>
          </div>
          <Divider style={{ marginTop: 16, marginBottom: 16 }} />
          {detail?.type === 'event' && detail?.event?.category && (
            <>
              <div style={{ paddingLeft: 16 }}>
                <Typography.Text type="secondary">Category</Typography.Text>
                <div><Tag>{detail.event.category}</Tag></div>
              </div>
              <Divider style={{ marginTop: 16, marginBottom: 16 }} />
            </>
          )}
        </div>
      </div>

      {/* Display Settings */}
      <div style={{ marginTop: 32 }}>
        <HeaderLabel label="Display Settings" />
        <div style={{
          background: 'white', padding: 16, display: 'flex', gap: 64,
        }}
        >
          <div>
            <Typography.Text type="secondary">Display Order</Typography.Text>
            <div><Typography.Text>{detail?.displayOrder ?? '-'}</Typography.Text></div>
          </div>
        </div>
      </div>

      <Divider style={{ marginTop: 32, marginBottom: 32 }} />
      <Flex justify="center">
        <AlumlyButton label="rxvWhite" width={128} onClick={() => navigate(RouteNameEnum.featuredContent.index)}>
          Back
        </AlumlyButton>
      </Flex>
    </div>
  );
};

export default FeaturedContentDetail;
